import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { fetchStateBreakdown } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];

// US states TopoJSON — served from public CDN, no API key needed
const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Full state name → abbreviation (us-atlas properties.name is always present)
const NAME_TO_ABBR = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','District of Columbia':'DC',
  'Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL',
  'Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA',
  'Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN',
  'Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV',
  'New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY',
  'North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR',
  'Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',
  'Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA',
  'Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY',
};

// State abbreviation → [longitude, latitude] centroid for dot placement
const STATE_CENTROIDS = {
  AL:[-86.8,32.8],AK:[-153.4,64.2],AZ:[-111.7,34.3],AR:[-92.4,34.9],CA:[-119.6,37.2],
  CO:[-105.5,39.0],CT:[-72.7,41.6],DE:[-75.5,39.0],DC:[-77.0,38.9],FL:[-81.5,27.8],
  GA:[-83.4,32.7],HI:[-157.5,20.3],ID:[-114.5,44.4],IL:[-89.2,40.0],IN:[-86.3,40.0],
  IA:[-93.5,42.1],KS:[-98.4,38.5],KY:[-84.9,37.5],LA:[-91.8,31.2],ME:[-69.4,45.4],
  MD:[-76.6,39.0],MA:[-71.5,42.3],MI:[-85.4,44.3],MN:[-94.4,46.4],MS:[-89.7,32.7],
  MO:[-92.5,38.5],MT:[-109.6,47.0],NE:[-99.9,41.5],NV:[-116.4,39.3],NH:[-71.6,43.7],
  NJ:[-74.5,40.1],NM:[-106.2,34.5],NY:[-75.4,43.0],NC:[-79.4,35.6],ND:[-100.5,47.5],
  OH:[-82.8,40.4],OK:[-97.5,35.6],OR:[-120.5,44.0],PA:[-77.2,40.9],RI:[-71.5,41.7],
  SC:[-80.9,33.8],SD:[-100.0,44.4],TN:[-86.3,35.9],TX:[-99.3,31.5],UT:[-111.1,39.3],
  VT:[-72.7,44.0],VA:[-78.2,37.5],WA:[-120.5,47.4],WV:[-80.6,38.6],WI:[-89.6,44.5],
  WY:[-107.3,43.0],
};

const MAX_DOT_R = 26; // max bubble radius in SVG units (viewport is ~975×610)

// Interpolate from light cream → brand orange based on 0–1 intensity
function heatColor(intensity) {
  if (!intensity) return '#e8eaed';
  const r = Math.round(0xf5 + (0xDC - 0xf5) * intensity);
  const g = Math.round(0xe8 + (0x58 - 0xe8) * intensity);
  const b = Math.round(0xd8 + (0x2A - 0xd8) * intensity);
  return `rgb(${r},${g},${b})`;
}

export default function StateBreakdown() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [noDates, setNoDates] = useState(false);
  const [sortBy, setSortBy] = useState('orders');
  const [view, setView] = useState('table');
  const [showDots, setShowDots] = useState(true);
  const [dimension, setDimension] = useState('ship');
  const [mapScale, setMapScale] = useState(60); // percent
  const [tooltip, setTooltip] = useState(null); // { x, y, abbr, orders }

  const load = () => {
    setLoading(true);
    setError(null);
    const params = { DisableDates: noDates };
    if (!noDates) {
      params.StartDate = toApiDate(start);
      params.EndDate = toApiDate(end);
    }
    params.Dimension = dimension;
    fetchStateBreakdown(params)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [dimension]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.stateBreakdowns ?? (Array.isArray(data) ? data : data?.data ?? []);
  const sorted = [...rows].sort((a, b) => {
    if (sortBy === 'orders') return Number(b.ordersCount ?? b.orderCount ?? 0) - Number(a.ordersCount ?? a.orderCount ?? 0);
    return (a.state ?? '').localeCompare(b.state ?? '');
  });

  const totalOrders = data?.totalOrders ?? rows.reduce((s, r) => s + Number(r.ordersCount ?? r.orderCount ?? 0), 0);

  // Build abbr → orders lookup for the map
  const ordersByState = {};
  rows.forEach(r => { ordersByState[r.state] = Number(r.ordersCount ?? r.orderCount ?? 0); });
  const maxOrders = Math.max(1, ...Object.values(ordersByState));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>State Breakdown</h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Orders by {dimension === 'billing' ? 'billing' : 'ship-to'} state (completed, excl. reships)</div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--gray-700)', cursor: 'pointer', alignSelf: 'center' }}>
            <input type="checkbox" checked={noDates} onChange={e => setNoDates(e.target.checked)} />
            All Time
          </label>
          {!noDates && (
            <>
              <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
            </>
          )}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>BY</div>
            <select value={dimension} onChange={e => setDimension(e.target.value)} className="form-select" style={{ fontSize: 11 }}>
              <option value="ship">Ship-to State</option>
              <option value="billing">Billing State</option>
            </select>
          </div>
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'States', value: rows.length },
            { label: 'Total Orders', value: totalOrders.toLocaleString() },
          ].map(({ label, value }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>By State {rows.length > 0 && `(${rows.length})`}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {/* View toggle */}
            {['table', 'map'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: view === v ? 'var(--orange)' : 'var(--gray-200)', color: view === v ? '#fff' : 'var(--gray-600)' }}>
                {v === 'table' ? '☰ Table' : '🗺 Heat Map'}
              </button>
            ))}
            {view === 'map' && (
              <>
                <button onClick={() => setShowDots(d => !d)} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: showDots ? 'var(--navy)' : 'var(--gray-200)', color: showDots ? '#fff' : 'var(--gray-600)' }}>
                  ⬤ Dots
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setMapScale(s => Math.max(40, s - 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', lineHeight: 1 }}>−</button>
                  <span style={{ fontSize: 10, color: 'var(--gray-500)', minWidth: 32, textAlign: 'center' }}>{mapScale}%</span>
                  <button onClick={() => setMapScale(s => Math.min(150, s + 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', lineHeight: 1 }}>+</button>
                </div>
              </>
            )}
            {view === 'table' && ['state', 'orders'].map(s => (
              <button key={s} onClick={() => setSortBy(s)} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: sortBy === s ? 'var(--navy)' : 'var(--gray-200)', color: sortBy === s ? '#fff' : 'var(--gray-600)' }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found.</div>
        )}

        {/* ── TABLE VIEW ── */}
        {view === 'table' && sorted.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>State</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>% of Orders</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => {
                  const orders = Number(row.ordersCount ?? row.orderCount ?? 0);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 700, fontSize: 13 }}>{row.state ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 700 }}>{orders.toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {totalOrders > 0 ? `${(orders / totalOrders * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── HEAT MAP VIEW ── */}
        {view === 'map' && rows.length > 0 && (
          <div style={{ padding: '12px 16px', position: 'relative', userSelect: 'none' }}>
            <ComposableMap
              projection="geoAlbersUsa"
              style={{ width: `${mapScale}%`, height: 'auto', display: 'block', margin: '0 auto' }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) => {
                  return geographies.map(geo => {
                    const name = geo.properties?.name;
                    const abbr = NAME_TO_ABBR[name];
                    const orders = abbr ? (ordersByState[abbr] ?? ordersByState[name] ?? 0) : (ordersByState[name] ?? 0);
                    const intensity = orders / maxOrders;
                    const color = heatColor(intensity);
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none' },
                          hover:   { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none', opacity: 0.8 },
                          pressed: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none' },
                        }}
                        onMouseEnter={e => setTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr: abbr ?? name, orders })}
                        onMouseLeave={() => setTooltip(null)}
                      />
                    );
                  });
                }}
              </Geographies>

              {/* ── PROPORTIONAL DOTS ── */}
              {showDots && Object.entries(ordersByState).map(([abbr, orders]) => {
                const coords = STATE_CENTROIDS[abbr];
                if (!coords || !orders) return null;
                const r = MAX_DOT_R * Math.sqrt(orders / maxOrders);
                return (
                  <Marker key={abbr} coordinates={coords}>
                    <circle
                      r={r}
                      fill="var(--navy)"
                      fillOpacity={0.65}
                      stroke="#fff"
                      strokeWidth={1}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => setTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr, orders })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                    {r >= 10 && (
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fontSize: Math.max(7, Math.min(11, r * 0.6)), fontWeight: 700, fill: '#fff', pointerEvents: 'none', fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {orders >= 1000 ? `${(orders / 1000).toFixed(1)}k` : orders}
                      </text>
                    )}
                  </Marker>
                );
              })}
            </ComposableMap>

            {/* Tooltip */}
            {tooltip && (
              <div style={{ position: 'absolute', left: tooltip.x + 12, top: tooltip.y - 10, background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                {tooltip.abbr}: {tooltip.orders.toLocaleString()} orders
                {totalOrders > 0 && <span style={{ fontWeight: 400, opacity: 0.8 }}> ({(tooltip.orders / totalOrders * 100).toFixed(1)}%)</span>}
              </div>
            )}

            {/* Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>0</span>
              <div style={{ width: 120, height: 10, borderRadius: 4, background: 'linear-gradient(to right, #f5e8d8, #DC582A)' }} />
              <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{maxOrders.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
